<template>
  <div class="calm">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">DMS · Phase 3</div>
          <h1 class="calm__header__title">{{ $t("lmg.dms_batch") }}</h1>
          <div class="calm__header__sub">
            <span><strong class="c-mono">{{ batches.length }}</strong> รายการ</span>
            <span class="calm__header__sep">·</span>
            <span class="c-mono">{{ todayStamp }}</span>
          </div>
        </div>
        <div class="calm__header__actions">
          <button class="c-btn c-btn--primary" @click="openCreate()">
            <i class="fas fa-plus"></i> {{ $t("new") }}
          </button>
        </div>
      </div>
    </header>

    <div class="calm__body">
      <!-- ═══════════════ TABLE CARD ═══════════════ -->
      <div class="calm__card calm__card--flush">
        <div class="invoice-table-head">
          <div>
            <div class="calm__card__title">{{ $t("lmg.dms_batch") }}</div>
            <div class="calm__card__hint" v-if="batches.length">
              {{ batches.length }} รายการ
            </div>
          </div>
          <div class="invoice-table-toolbar">
            <input
              v-model="searchBatchNumber"
              type="text"
              class="ed c-mono"
              :placeholder="$t('lmg.batch_number')"
              style="width:220px;max-width:100%"
              :disabled="isLoading"
              @keyup.enter="loadBatches()"
            />
            <!-- ปี / เดือน ที่นำส่ง (batch_date). ปีอย่างเดียว = ทั้งปี,
                 ปี+เดือน = เฉพาะเดือนนั้น. เดือนถูก disable จนกว่าจะเลือกปี —
                 เดือนที่ไม่มีปีไม่มีความหมาย. -->
            <el-select
              v-model="filterYear"
              style="width:130px"
              :placeholder="$t('lmg.dms_filter_year')"
              :disabled="isLoading"
              clearable
              @change="onFilterYearChange"
            >
              <el-option
                v-for="y in filterYearOptions"
                :key="y"
                :label="String(y)"
                :value="y"
              ></el-option>
            </el-select>
            <el-select
              v-model="filterMonth"
              style="width:150px"
              :placeholder="filterYear ? $t('lmg.dms_filter_all_months') : $t('lmg.dms_filter_year_first')"
              :disabled="isLoading || !filterYear"
              clearable
            >
              <el-option
                v-for="o in monthOptions"
                :key="o.ID"
                :label="o.Name"
                :value="o.ID"
              ></el-option>
            </el-select>
            <button class="c-btn" :disabled="isLoading" @click="loadBatches()">
              <i class="fas fa-spinner fa-spin" v-if="isLoading"></i>
              <i class="fas fa-sync" v-else></i>
              {{ $t("search") }}
            </button>
            <button
              class="c-btn"
              v-if="hasListFilter"
              :disabled="isLoading"
              @click="clearListFilters()"
            >
              <i class="fas fa-times"></i>
              {{ $t("lmg.dms_filter_clear") }}
            </button>
          </div>
        </div>

        <el-table
          :data="batches"
          row-key="id"
          :empty-text="isLoading ? 'กำลังโหลด…' : 'ไม่พบรายการ'"
        >
          <el-table-column :label="$t('lmg.batch_number')" min-width="170">
            <div slot-scope="{ row }">
              <span
                class="c-mono"
                :class="{ 'dms-draft-number': row.status === 1 }"
              >{{ row.batch_number || ('#' + row.id) }}</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.batch_date')" min-width="130">
            <div slot-scope="{ row }">
              <span class="c-mono">{{ row.BatchDateShow || formatDateShow(row.batch_date) }}</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.round')" min-width="90" align="center">
            <div slot-scope="{ row }">
              <span v-if="row.round">{{ roundLabel(row.round) }}</span>
              <span v-else class="c-mute">—</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.dms_status')" min-width="130">
            <div slot-scope="{ row }">
              <span class="calm__pill" :class="statusPillClass(row.status)">
                {{ statusLabel(row.status) }}
              </span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.dms_total')" min-width="140" align="right">
            <div slot-scope="{ row }">
              <span class="c-mono money">{{ formatBaht(row.total_amount) }}</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.dms_item_count')" min-width="110" align="center">
            <div slot-scope="{ row }">
              <span class="c-mono">{{ row.item_count != null ? row.item_count : (row.Items ? row.Items.length : 0) }}</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('actions')" min-width="160" align="right">
            <div slot-scope="{ row }" class="row-actions">
              <button class="rowbtn" :title="$t('lmg.view_detail')" @click="openDetail(row)">
                <i class="fas fa-eye"></i>
              </button>
              <button
                class="rowbtn"
                :title="$t('lmg.export_pdf')"
                :disabled="isExporting(row.id, 'pdf')"
                @click="exportBatch(row, 'pdf')"
              >
                <i class="fas fa-spinner fa-spin" v-if="isExporting(row.id, 'pdf')"></i>
                <i class="fas fa-file-pdf" v-else></i>
              </button>
              <button
                class="rowbtn"
                :title="$t('lmg.export_xlsx')"
                :disabled="isExporting(row.id, 'xlsx')"
                @click="exportBatch(row, 'xlsx')"
              >
                <i class="fas fa-spinner fa-spin" v-if="isExporting(row.id, 'xlsx')"></i>
                <i class="fas fa-file-excel" v-else></i>
              </button>
              <button
                class="rowbtn"
                v-if="row.filename"
                title="PDF"
                @click="viewFile(row)"
              >
                <i class="fa fa-download"></i>
              </button>
              <button
                class="rowbtn"
                v-if="row.status == 1"
                title="นำส่ง"
                :disabled="isRowBusy(row.id)"
                @click="confirmSubmit(row)"
              >
                <i class="fas fa-spinner fa-spin" v-if="isRowBusyWith(row.id, 'submit')"></i>
                <i class="fas fa-paper-plane" v-else></i>
              </button>
              <button
                class="rowbtn"
                v-if="row.status != 9"
                title="แก้ไข"
                @click="openEdit(row)"
              >
                <i class="fas fa-pen"></i>
              </button>
              <button
                class="rowbtn"
                v-if="row.status != 9"
                :title="$t('lmg.delete')"
                :disabled="isRowBusy(row.id)"
                @click="confirmDelete(row)"
              >
                <i class="fas fa-spinner fa-spin" v-if="isRowBusyWith(row.id, 'delete')"></i>
                <i class="fas fa-trash" v-else></i>
              </button>
              <span v-else class="c-mute c-mono dms-deleted">
                {{ row.deleted_by || '' }}
              </span>
            </div>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- ═══════════════ Detail Modal ═══════════════ -->
    <modal :show.sync="showDetailModal" modal-classes="modal-secondary modal-lg">
      <div class="invoice-detail">
        <div class="detail-header">
          <div>
            <h4>
              <i class="fas fa-truck mr-2"></i>{{ $t("lmg.dms_batch") }}
              <span class="detail-id">{{ detail.batch_number || ('#' + detail.id) }}</span>
            </h4>
          </div>
          <div class="d-flex align-items-center">
            <span :class="detailStatusClass(detail.status)" class="mr-2">
              {{ statusLabel(detail.status) }}
            </span>
            <base-button
              v-if="detail.id"
              size="sm"
              type="danger"
              :title="$t('lmg.export_pdf')"
              :disabled="isExporting(detail.id, 'pdf')"
              @click="exportBatch(detail, 'pdf')"
            >
              <i class="fas fa-spinner fa-spin mr-1" v-if="isExporting(detail.id, 'pdf')"></i>
              <i class="fas fa-file-pdf mr-1" v-else></i>PDF
            </base-button>
            <base-button
              v-if="detail.id"
              size="sm"
              type="success"
              :title="$t('lmg.export_xlsx')"
              :disabled="isExporting(detail.id, 'xlsx')"
              @click="exportBatch(detail, 'xlsx')"
            >
              <i class="fas fa-spinner fa-spin mr-1" v-if="isExporting(detail.id, 'xlsx')"></i>
              <i class="fas fa-file-excel mr-1" v-else></i>Excel
            </base-button>
            <base-button
              v-if="detail.filename"
              size="sm"
              type="warning"
              :title="$t('download')"
              @click="viewFile(detail)"
            >
              <i class="fas fa-file-pdf mr-1"></i>PDF
            </base-button>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-section-header"><i class="fas fa-info-circle"></i>{{ $t("lmg.dms_batch") }}</div>
          <div class="row">
            <div class="col-lg-4">
              <base-input :label="$t('lmg.batch_number')" :value="detail.batch_number || '-'" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.batch_date')" :value="detail.BatchDateShow || formatDateShow(detail.batch_date) || '-'" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.round')" :value="detail.round ? roundLabel(detail.round) : '-'" readonly></base-input>
            </div>
          </div>
          <div class="row">
            <div class="col-lg-4">
              <base-input :label="$t('lmg.month')" :value="detail.month || '-'" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.year')" :value="detail.year || '-'" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.dms_total')" :value="formatBaht(detail.total_amount)" readonly></base-input>
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="detailItems && detailItems.length">
          <div class="detail-section-header">
            <i class="fas fa-list"></i>{{ $t('lmg.select_policies') }} ({{ detailItems.length }})
          </div>
          <div class="dms-type-summary" v-if="detailTypeCounts.length">
            <span
              v-for="t in detailTypeCounts"
              :key="t.type"
              class="dms-type-chip"
            >
              <strong class="c-mono">{{ t.type }}</strong>
              <span class="c-soft"> · {{ t.count }} รายการ</span>
            </span>
          </div>
          <el-table ref="detailTable" :data="detailItems" row-key="ID" header-row-class-name="thead-light" size="mini">
            <el-table-column :label="$t('lmg.policy_number')" min-width="170">
              <template slot-scope="{ row }">
                <span class="c-mono">{{ row.PolicyNumber || row.policy_number || ('#' + (row.TransactionID || row.transaction_id)) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.premium_original')" min-width="120" align="right">
              <template slot-scope="{ row }">
                {{ formatBaht(row.PremiumOriginal != null ? row.PremiumOriginal : row.premium_original) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.premium_adjusted')" min-width="120" align="right">
              <template slot-scope="{ row }">
                {{ formatBaht(row.PremiumAdjusted != null ? row.PremiumAdjusted : row.premium_adjusted) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.adjust_note')" min-width="160">
              <template slot-scope="{ row }">
                <span class="c-soft">{{ row.AdjustNote || row.adjust_note || '—' }}</span>
              </template>
            </el-table-column>
            <!-- วันชำระ — date the customer's payment for this billing was
                 received (payment.created_at via billing.payment_id, joined
                 read-only by DmsBatchDetail; the API sends "" when unpaid).
                 Unpaid billing → "ยังไม่ชำระ", muted: it is the NORMAL state
                 (~89% of prod DMS items — the branch advances the premium),
                 not an alarm, so never error-red.
                 Manual line (billing_id <= 0) → "—": it has no billing, so
                 there is nothing that could be paid or unpaid. -->
            <el-table-column :label="$t('lmg.dms_paid_date')" min-width="110" align="center">
              <template slot-scope="{ row }">
                <span class="c-mono" v-if="row.PaidAtShow">{{ row.PaidAtShow }}</span>
                <span class="c-mute" v-else-if="isManualDmsItem(row)">—</span>
                <span class="c-soft" v-else>{{ $t('lmg.dms_unpaid') }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="detail-section" v-if="detail.note">
          <div class="detail-section-header"><i class="fas fa-sticky-note"></i>{{ $t('lmg.note') }}</div>
          <div class="row">
            <div class="col-lg-12">
              <base-input :value="detail.note" readonly></base-input>
            </div>
          </div>
        </div>
      </div>

      <template slot="footer">
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showDetailModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- ═══════════════ Create Modal ═══════════════ -->
    <modal :show.sync="showCreateModal" modal-classes="modal-secondary modal-xl calm-modal dms-picker-modal">
      <div class="calm calm-modal-body">
        <div class="bm-head">
          <div>
            <div class="c-eyebrow">{{ editingBatchId ? "Edit DMS batch" : "Create DMS batch" }}</div>
            <h2 class="bm-title">
              <i class="fas fa-truck"></i>
              {{ editingBatchId ? "แก้ไขใบนำส่ง DMS" : $t("lmg.dms_batch") }}
            </h2>
            <div class="c-soft" style="font-size:13px">เลือกกรมธรรม์ที่ต้องการนำส่ง — แก้เบี้ยได้</div>
          </div>
          <div class="bm-meta" v-if="selectedPolicies.length">
            <div class="c-eyebrow">เลือกแล้ว</div>
            <div class="bm-meta__value">
              <strong class="c-mono">{{ selectedPolicies.length }}</strong>
              <span class="c-soft" style="font-size:13px;margin-left:6px">รายการ</span>
            </div>
          </div>
        </div>

        <!-- Batch info -->
        <div class="bm-section">
          <div class="bm-section__head">
            <div class="c-eyebrow">Batch info</div>
          </div>
          <div class="calm__filter-grid bm-filter-grid">
            <div class="fld">
              <label>{{ $t("lmg.batch_number") }}</label>
              <base-input
                v-model="form.batchNumber"
                input-classes="c-mono"
                :placeholder="$t('lmg.batch_number')"
              ></base-input>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.batch_date") }}</label>
              <flat-picker
                class="form-control datepicker"
                v-model="form.batchDate"
                :config="dateConfig"
                :placeholder="$t('lmg.batch_date')"
              ></flat-picker>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.round") }}</label>
              <el-select v-model="form.round" :placeholder="$t('lmg.round')" clearable>
                <el-option v-for="o in roundOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
              </el-select>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.month") }}</label>
              <el-select v-model="form.month" :placeholder="$t('lmg.month')">
                <el-option v-for="o in monthOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
              </el-select>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.year") }}</label>
              <el-select v-model="form.year" :placeholder="$t('lmg.year')">
                <el-option v-for="o in yearOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
              </el-select>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.dms_pdf") }}</label>
              <file-input @change="filesChange"></file-input>
              <div class="c-mute" style="font-size:12px;margin-top:4px">PDF/รูป ≤ 5MB</div>
            </div>
          </div>
          <div class="bm-note" style="margin-top:14px">
            <div class="fld">
              <label>{{ $t("lmg.note") }}</label>
              <input type="text" class="ed" v-model="form.note" :placeholder="$t('lmg.note')" />
            </div>
          </div>
        </div>

        <!-- Policy picker -->
        <div class="bm-section bm-section--table bm-section--picker">
          <div class="bm-section__head">
            <div class="c-eyebrow">{{ $t('lmg.select_policies') }}</div>
            <div class="c-soft" style="font-size:12.5px">
              <span
                v-if="availableTruncated"
                class="calm__pill calm__pill--warning dms-trunc"
              >
                แสดง <strong class="c-mono">{{ formatCount(available.length) }}</strong>
                จาก <strong class="c-mono">{{ formatCount(availableTotal) }}</strong>
                รายการ — กรุณากรองเพิ่ม
              </span>
              <span v-else-if="(available || []).length">
                พบ <strong class="c-mono">{{ available.length }}</strong> กรมธรรม์ ·
                ติ๊กที่ต้องการนำส่ง · แก้เบี้ยได้
              </span>
              <span v-else-if="isSearchingPolicies">กำลังค้นหา…</span>
              <span v-else-if="hasSearchedPolicies">ไม่พบกรมธรรม์ว่าง</span>
              <span v-else>กดค้นหาเพื่อหากรมธรรม์</span>
            </div>
          </div>

          <!-- Search-on-demand bar — sticky (see .bm-picker-search in <style>):
               with 70+ policies ticked the selected card + results table are far
               taller than the viewport, so this row pins to the top of the modal
               instead of scrolling away. -->
          <div class="bm-picker-search">
            <!-- ประเภทประกัน — layered picker (เลือก layer ไหนก็ได้ = กรองทุกสินค้าใต้ layer) -->
            <product-picker
              class="bm-picker-search__insure"
              :value="form.insuranceNodeID"
              :sellable-ids="sellableLeafIds"
              :loading="!catalogReady"
              :allow-any-node="true"
              @input="onPickInsuranceFilter"
            ></product-picker>
            <input
              type="text"
              class="ed bm-picker-search__input"
              v-model="form.policySearch"
              placeholder="ค้นหาหมายเลขกรมธรรม์"
              @keyup.enter="searchPolicies"
            />
            <flat-picker
              class="form-control datepicker bm-picker-search__date"
              v-model="form.saleStart"
              :config="dateConfig"
              placeholder="ขายตั้งแต่วันที่"
            ></flat-picker>
            <flat-picker
              class="form-control datepicker bm-picker-search__date"
              v-model="form.saleEnd"
              :config="dateConfig"
              placeholder="ถึงวันที่"
            ></flat-picker>
            <el-select
              v-model="form.round"
              :placeholder="$t('lmg.round')"
              class="bm-picker-search__round"
              clearable
            >
              <el-option v-for="o in roundOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
            <button
              class="c-btn c-btn--primary"
              :disabled="isSearchingPolicies"
              @click="searchPolicies"
            >
              <i class="fas fa-spinner fa-spin" v-if="isSearchingPolicies"></i>
              <i class="fas fa-search" v-else></i>
              {{ $t("search") }}
            </button>
            <button
              v-if="hasPickerFilters"
              class="c-btn c-btn--ghost"
              :disabled="isSearchingPolicies"
              @click="clearPickerFilters"
            >
              <i class="fas fa-times mr-1"></i>ล้างตัวกรอง
            </button>
            <span class="bm-picker-search__count c-soft" v-if="selectedPolicies.length">
              เลือกแล้ว <strong class="c-mono">{{ selectedPolicies.length }}</strong> กรมธรรม์
            </span>
          </div>

          <!-- Policies removed from the selection because another batch delivered
               them first. Never silent: submit() builds Items straight from
               selectedPolicies, and on the edit path DmsBatchUpdate hard-deletes
               the batch's items AND their ledger rows before re-inserting only
               what the payload carries — a line that disappears here moves the
               branch pool with nothing on screen to catch it against.
               It therefore SURVIVES searching: it is retired only by the ✕
               (operator acknowledgement), by ceasing to be true, or by the
               selection going away. See pruneStolenRemoved. -->
          <div class="bm-blocked bm-blocked--warn" v-if="stolenRemoved.length">
            <div class="bm-blocked__head">
              <i class="fas fa-exclamation-triangle"></i>
              <span>
                <strong class="c-mono">{{ formatCount(stolenRemoved.length) }}</strong>
                กรมธรรม์ที่เลือกไว้ ถูกนำส่งโดยใบนำส่งอื่นไปแล้ว
                จึง<strong>ถูกเอาออกจากรายการที่เลือก</strong> — กรุณาตรวจสอบก่อนบันทึก
              </span>
              <button
                type="button"
                class="bm-blocked__ack"
                title="รับทราบแล้ว — ซ่อนคำเตือนนี้"
                @click="acknowledgeStolen"
              >
                รับทราบ
              </button>
            </div>
            <div class="bm-blocked__row" v-for="s in stolenRemoved" :key="s.billing_id">
              <span class="c-mono bm-blocked__policy">
                {{ s.policy_number || ('#' + s.transaction_id) }}
              </span>
              <span class="bm-blocked__where">
                ถูกนำส่งในชุด DMS เลขที่
                <strong class="c-mono">{{ s.batch_number || ('#' + s.batch_id) }}</strong>
              </span>
              <span class="calm__pill" :class="batchStatusPillClass(s.batch_status)">
                {{ batchStatusLabel(s.batch_status) }}
              </span>
            </div>
          </div>

          <!-- "ทำไมค้นแล้วไม่เจอ" — กรมธรรม์ที่อยู่ในชุดนำส่ง DMS แล้ว.
               These are NOT rows: the BE returns them on Datum.blocked and keeps
               them out of Data on purpose, so a blocked policy is structurally
               un-tickable here.

               WHERE THIS SITS IN THE DUPLICATE GUARD (read before weakening it):
               the authoritative refusal is on the SERVER. dmsItemsInOtherBatchTx
               runs SELECT ... FOR UPDATE inside the same transaction as the
               insert, in BOTH DmsBatchCreate and DmsBatchUpdate, and any billing
               already sitting in a live batch of this branch makes the whole
               request roll back with 400 ERROR_DMS_BILLING_IN_OTHER_BATCH. Its
               exact scope: same branch only; batches with status CANCELLED do
               not block; Update excludes the batch being edited (an edit may
               legitimately re-submit its own items); and the row limit on that
               query caps how many conflicts the Thai message NAMES, not whether
               the request is refused — one conflict is enough to refuse all.

               This filter is therefore an EARLY WARNING, not the guard. It is
               strictly weaker than the server check and cannot be relied on
               alone: it only ever sees policies matching the current search
               filter, the BE caps Datum.blocked at 50 rows (blocked_total is the
               real count), and it is a read taken before the operator submits —
               another branch user can batch a policy in between. Keep it because
               catching a clash at tick time beats a rejected save after the
               operator has assembled 70 lines, and because it explains the empty
               result: it replaced the bare "ไม่พบ" that made staff think a
               cancellation had failed to release the policy. -->
          <div class="bm-blocked" v-if="blockedShown.length">
            <div class="bm-blocked__head">
              <i class="fas fa-info-circle"></i>
              <span>
                มี <strong class="c-mono">{{ formatCount(blockedTotal || blockedShown.length) }}</strong>
                กรมธรรม์ที่ตรงกับคำค้น แต่<strong>อยู่ในชุดนำส่ง DMS แล้ว</strong> — นำส่งซ้ำไม่ได้
              </span>
            </div>
            <div class="bm-blocked__row" v-for="b in blockedShown" :key="b.billing_id">
              <span class="c-mono bm-blocked__policy">
                {{ b.policy_number || ('#' + b.transaction_id) }}
              </span>
              <span class="bm-blocked__where">
                <template v-if="isOwnBatch(b)">อยู่ในใบนำส่งฉบับนี้แล้ว</template>
                <template v-else>
                  อยู่ในชุดนำส่ง DMS เลขที่
                  <strong class="c-mono">{{ b.batch_number || ('#' + b.batch_id) }}</strong>
                </template>
              </span>
              <span class="calm__pill" :class="batchStatusPillClass(b.batch_status)">
                {{ batchStatusLabel(b.batch_status) }}
              </span>
              <span class="c-mono c-soft bm-blocked__date" v-if="b.batch_date">
                {{ b.batch_date }}
              </span>
            </div>
            <div class="c-mute bm-blocked__more" v-if="blockedTruncated">
              แสดง <strong class="c-mono">{{ formatCount(blockedShown.length) }}</strong>
              จาก <strong class="c-mono">{{ formatCount(blockedTotal) }}</strong>
              รายการ — พิมพ์หมายเลขกรมธรรม์ให้ละเอียดขึ้นเพื่อดูรายการที่เหลือ
            </div>
          </div>

          <div class="bm-table-wrap">
            <el-table
              ref="pickerTable"
              :data="available"
              row-key="billing_id"
              @selection-change="onSelectionChange"
              :empty-text="pickerEmptyText"
            >
              <!-- reserve-selection is kept deliberately: without it el-table's
                   setData() calls clearSelection() on every data swap and EMITS
                   selection-change([]) from the data watcher, which we cannot
                   guard — it would wipe the on-screen half of the selection.
                   With it, setData is silent and syncTableSelection() owns the
                   table's selection outright. -->
              <el-table-column type="selection" align="center" width="48" reserve-selection></el-table-column>
              <el-table-column :label="$t('lmg.policy_number')" min-width="190">
                <template slot-scope="{ row }">
                  <span class="c-mono dms-nowrap">{{ row.policy_number || ('#' + row.transaction_id) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="ประเภทประกัน" min-width="130">
                <template slot-scope="{ row }">
                  <span class="c-soft">{{ row.insurance_name || '—' }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.customer')" min-width="140">
                <template slot-scope="{ row }">
                  <div class="stack__primary">{{ row.customer_fname }} {{ row.customer_lname }}</div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.owner_person')" min-width="140">
                <template slot-scope="{ row }">
                  <div class="stack__primary">{{ row.agent_fname }} {{ row.agent_lname }}</div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.premium_original')" min-width="110" align="right">
                <template slot-scope="{ row }">
                  <span class="c-mono">{{ formatBaht(row.net_amount) }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.premium_adjusted')" min-width="130" align="right">
                <template slot-scope="{ row }">
                  <el-input
                    v-if="isSelected(row)"
                    size="mini"
                    type="number"
                    v-model="premiumAdjusted[row.billing_id]"
                    class="dms-num"
                  ></el-input>
                  <span v-else class="c-mute c-mono">{{ formatBaht(row.net_amount) }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.adjust_note')" min-width="140">
                <template slot-scope="{ row }">
                  <el-input
                    v-if="isSelected(row)"
                    size="mini"
                    v-model="adjustNote[row.billing_id]"
                    :placeholder="$t('lmg.adjust_note')"
                  ></el-input>
                  <span v-else class="c-mute">—</span>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Selected-items card — เอาออกได้ทีละรายการ (แม้ไม่อยู่ในผลค้นหาปัจจุบัน).
               Height-capped with its own scrollbar: staff routinely tick 70+
               policies and an uncapped card pushed the search row and the results
               table right off the screen.
               Sits BELOW the results table (operator item "ออกมาด้านนอก"): the
               found policies come straight under the sticky search bar, so a
               search result is visible and tickable without scrolling past the
               selection, however many policies are already selected. -->
          <div class="bm-selected" v-if="selectedPolicies.length">
            <div class="bm-selected__head">
              <div class="c-eyebrow">
                กรมธรรม์ที่เลือก
                <strong class="c-mono">{{ selectedPolicies.length }}</strong>
              </div>
              <!-- per-insurance-type count of the selection (same derivation as
                   the detail view's chips — countByPolicyType). -->
              <div class="dms-type-summary dms-type-summary--picker" v-if="selectedTypeCounts.length">
                <span
                  v-for="t in selectedTypeCounts"
                  :key="t.type"
                  class="dms-type-chip"
                >
                  <strong class="c-mono">{{ t.type }}</strong>
                  <span class="c-soft"> · {{ t.count }} รายการ</span>
                </span>
              </div>
            </div>
            <div class="bm-selected__list">
              <div
                v-for="p in selectedPolicies"
                :key="p.billing_id"
                class="bm-chip"
              >
                <span class="c-mono bm-chip__policy">
                  {{ p.policy_number || ('#' + p.transaction_id) }}
                </span>
                <span class="c-soft bm-chip__name">
                  {{ p.customer_fname }} {{ p.customer_lname }}
                </span>
                <span class="c-mono bm-chip__premium">
                  {{ formatBaht(premiumAdjusted[p.billing_id] != null ? premiumAdjusted[p.billing_id] : p.net_amount) }}
                </span>
                <button
                  type="button"
                  class="bm-chip__x"
                  title="เอาออก"
                  @click="removeSelected(p)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="calm calm-modal-foot">
          <button type="button" class="c-btn c-btn--ghost" @click="showCreateModal = false">
            {{ $t("close") }}
          </button>
          <button
            v-if="!editingBatchId"
            type="button"
            class="c-btn"
            :disabled="!selectedPolicies.length || submitting"
            @click="submit(1)"
          >
            <i class="fas fa-spinner fa-spin" v-if="submitting"></i>
            <i class="fas fa-save" v-else></i>
            บันทึกร่าง
            <span v-if="selectedPolicies.length" style="opacity:0.8">· {{ selectedPolicies.length }}</span>
          </button>
          <button
            type="submit"
            class="c-btn c-btn--primary"
            :disabled="!selectedPolicies.length || submitting"
            @click="submit(2)"
          >
            <i class="fas fa-spinner fa-spin" v-if="submitting"></i>
            <i class="fas fa-check" v-else></i>
            {{ editingBatchId ? "บันทึกการแก้ไข" : ("นำส่ง · " + $t("lmg.submit_dms")) }}
            <span v-if="selectedPolicies.length" style="opacity:0.8">· {{ selectedPolicies.length }}</span>
          </button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Input } from "element-ui";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import FileInput from "@/components/Inputs/FileInput";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import ProductPicker from "@/components/lmg/insurance/ProductPicker.vue";
import { LmgService } from "@/resource";

const MAX_PDF_BYTES = 5 * 1024 * 1024; // 5MB

// How many years back the ปี filter always offers, on top of whatever the loaded
// batches show. Deriving the list purely from the data creates a chicken-and-egg
// trap: a year only becomes selectable once a batch already carries a วันที่นำส่ง
// in it, but an operator filing a delivery backdated into last year needs to pick
// that year to check whether one exists. Two years back covers re-filing an older
// round; anything earlier still appears on its own as soon as one batch lands
// there. Bump this rather than hardcoding a specific year, which would rot.
const FILTER_YEARS_BACK = 2;

// Per-insurance-type count of a list of policy numbers, shared by the detail
// modal and the create/edit picker. Insurance type = 2nd hyphen-segment of
// policy_number (24-AVx-xxx-YYYY-XX → AVx). Policy numbers that don't match the
// format are bucketed as "อื่นๆ". Sorted by type → [{ type, count }].
function countByPolicyType(policyNumbers) {
  const map = {};
  (policyNumbers || []).forEach((pn) => {
    const parts = String(pn || "").split("-");
    const type = parts.length >= 2 && parts[1] ? parts[1] : "อื่นๆ";
    map[type] = (map[type] || 0) + 1;
  });
  return Object.keys(map)
    .sort()
    .map((type) => ({ type, count: map[type] }));
}

export default {
  components: {
    flatPicker,
    FileInput,
    ProductPicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  computed: {
    ...mapGetters({
      batches: "Dms/getBatches",
      available: "Dms/getAvailable",
      availableTotal: "Dms/getAvailableTotal",
      blocked: "Dms/getBlocked",
      blockedTotal: "Dms/getBlockedTotal",
      isLoading: "Dms/isLoading",
      isLoadingAvailable: "Dms/isLoadingAvailable",
    }),
    // Insurance catalog (for the layered product-picker filter). Mirrors the
    // sales form: getData = sellable leaves, catalogRoots = tree loaded marker,
    // catalogChildrenOf = Map UpperID→children[] used to expand a chosen node.
    ...mapGetters("Insure", {
      tableInsure: "getData",
      catalogRoots: "catalogRoots",
      catalogChildrenOf: "catalogChildrenOf",
    }),
    catalogReady() {
      return !!(this.catalogRoots && this.catalogRoots.length > 0);
    },
    // Any list filter active? Drives the ล้างตัวกรอง button, which is hidden
    // when there is nothing to clear.
    hasListFilter() {
      return !!(
        String(this.searchBatchNumber || "").trim() ||
        this.filterYear ||
        this.filterMonth
      );
    },
    // The BE caps policies-available at 2000 rows. When the pre-limit total it
    // reports exceeds what we actually received, the operator is looking at a
    // partial list and must narrow the filters — otherwise a policy that is
    // simply off the end reads as "already delivered". Falls back to
    // total === rows.length (no warning) when the BE doesn't send a total.
    availableTruncated() {
      return Number(this.availableTotal || 0) > (this.available || []).length;
    },
    // Already-delivered matches to explain in the notice. Defensive against a
    // malformed/legacy Datum: anything that isn't an array reads as "none", which
    // is exactly the pre-change behaviour.
    blockedShown() {
      return Array.isArray(this.blocked) ? this.blocked : [];
    },
    // The BE caps `blocked` at 50 rows but reports the pre-limit blocked_total.
    blockedTruncated() {
      return Number(this.blockedTotal || 0) > this.blockedShown.length;
    },
    sellableLeafIds() {
      return (this.tableInsure || []).map((o) => o.ID);
    },
    // true when any picker filter (insurance/policy/date) is active → show "ล้าง"
    hasPickerFilters() {
      return !!(
        this.form.insuranceNodeID ||
        this.form.policySearch ||
        this.form.saleStart ||
        this.form.saleEnd
      );
    },
    todayStamp() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    },
    // empty-state copy for the policy picker, by search lifecycle stage
    pickerEmptyText() {
      if (this.isSearchingPolicies) return "กำลังค้นหา…";
      if (!this.hasSearchedPolicies) return "พิมพ์หมายเลขกรมธรรม์แล้วกดค้นหา";
      // An empty list with a blocked notice above it is not "nothing found" — the
      // reason is right there, so point at it instead of repeating the dead end.
      if (this.blockedShown.length) {
        return "ไม่มีกรมธรรม์ว่างที่ตรงกับคำค้น — ดูรายการที่อยู่ในชุดนำส่งแล้วด้านบน";
      }
      return "ไม่พบกรมธรรม์ว่าง";
    },
    // Per-insurance-type count for the detail modal — see countByPolicyType.
    detailTypeCounts() {
      return countByPolicyType(
        (this.detailItems || []).map((it) => it.PolicyNumber || it.policy_number)
      );
    },
    // Same per-type count for the CURRENTLY SELECTED policies in the
    // create/edit picker (operator: "ตรงที่จำแนกจำนวน DMS หายไป"). Derived from
    // selectedPolicies, so it follows every tick / untick / chip ✕ / prune live.
    // Display only — never read by submit().
    selectedTypeCounts() {
      return countByPolicyType(
        (this.selectedPolicies || []).map((p) => p.policy_number)
      );
    },
  },
  data() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return {
      showCreateModal: false,
      showDetailModal: false,
      // null = create flow; batch id = edit flow (PUT instead of POST)
      editingBatchId: null,
      detail: {},
      detailItems: [],
      // list filter — batch-number LIKE, sent to BE as `batchNumber` (empty = no filter)
      searchBatchNumber: "",
      // list filter — ปี / เดือน ที่นำส่ง, matched against batch_date (the date
      // this table shows and the date on the delivery document), NOT created_at
      // and NOT the month/year columns: all three diverge in production.
      // "" = not filtering. เดือน is only meaningful together with a ปี.
      filterYear: "",
      filterMonth: "",
      // Was the request that produced the rows currently in the store completely
      // unfiltered? Only such a response is a valid census of which years exist,
      // so only it may shrink the ปี dropdown (see harvestFilterYears). Tracks
      // the request, NOT what is sitting in the selects — search is button/enter
      // driven, so the two differ between typing and searching. The mounted()
      // load is unfiltered, hence true.
      appliedUnfiltered: true,
      // ปี options, derived from the loaded data so the list cannot rot. Seeded
      // by the first (unfiltered) load in mounted().
      filterYearOptions: [],
      // create form
      form: {
        batchNumber: "",
        batchDate: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
        round: "",
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        note: "",
        // policy picker — search-on-demand filter (policy-number LIKE, sent to BE as `policy`)
        policySearch: "",
        // insurance-type filter — the chosen catalog node ID (any layer); the
        // ProductPicker trigger renders from this. insuranceIDs holds the CSV of
        // that node + all descendants, sent to BE as `insuranceIDs`.
        insuranceNodeID: 0,
        insuranceIDs: "",
        // sale_date range (default empty = no filter), sent as startDate/endDate
        saleStart: "",
        saleEnd: "",
      },
      pdfFile: null,
      // policy picker — full row objects of ticked policies
      selectedPolicies: [],
      // per-row editable maps, keyed by billing_id
      premiumAdjusted: {},
      adjustNote: {},
      // policy-picker search state
      isSearchingPolicies: false,
      hasSearchedPolicies: false,
      // true while syncTableSelection() is pushing selectedPolicies INTO the
      // el-table. The store emits selection-change synchronously on every single
      // toggle, so without this guard a half-finished sync would be read back as
      // the operator's intent (see onSelectionChange).
      syncingSelection: false,
      // Policies that were ticked but have since been delivered on ANOTHER batch,
      // so syncTableSelection() had to take them back out of the selection. Shown
      // to the operator — a settlement document quietly losing a line is exactly
      // the kind of change nobody may discover from a row count.
      stolenRemoved: [],
      submitting: false,
      // per-batch export loading state, keyed by `${id}:${kind}` (pdf|xlsx)
      exportingKeys: {},
      // Per-batch MUTATION in flight, keyed by batch id → 'submit' | 'delete'.
      // นำส่ง applies the branch ledger (ตัดยอดเงินกองกลางสาขา), so firing it
      // twice debits the pool twice for one batch — the shape of the 5.36M
      // over-deduction. The confirm dialog closes before the PUT resolves, which
      // leaves the row button live and the whole flow re-clickable; this key is
      // what makes the second click impossible. One key per BATCH, not per
      // action: submit and delete both mutate the same batch, so neither may
      // start while the other is outstanding.
      rowBusy: {},
      dateConfig: { dateFormat: "Y-m-d", allowInput: true },
      roundOptions: [
        { ID: 1, Name: "รอบที่ 1 (วันที่ 15)" },
        { ID: 2, Name: "รอบที่ 2 (สิ้นเดือน)" },
      ],
      monthOptions: Array.from({ length: 12 }, (_, i) => ({
        ID: i + 1,
        Name: pad(i + 1),
      })),
      yearOptions: Array.from({ length: 3 }, (_, i) => {
        const y = now.getFullYear() - 1 + i;
        return { ID: y, Name: String(y) };
      }),
    };
  },
  watch: {
    // Keep the ปี dropdown derived from the data rather than a hardcoded list.
    // Fires on the mounted() load and on every search.
    batches(rows) {
      this.harvestFilterYears(rows);
    },
    // Element UI computes el-table column widths from the DOM. While the modal
    // is display:none the table measures as 0/wrong, which makes the selection
    // checkbox overlap the policy-number column. Re-run doLayout() once the
    // modal's open transition has finished and the table is actually visible.
    showCreateModal(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.pickerTable) this.$refs.pickerTable.doLayout();
          }, 320); // after the modal open transition
        });
      } else {
        // don't leak edit state after closing via the close/× buttons
        this.editingBatchId = null;
      }
    },
    showDetailModal(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.detailTable) this.$refs.detailTable.doLayout();
          }, 320);
        });
      }
    },
  },
  async mounted() {
    await this.loadBatchesAction();
    // Seed the ปี dropdown. The batches watcher normally does this, but an empty
    // response can leave the getter at the same value it started on, so seed once
    // explicitly instead of relying on a change firing.
    this.harvestFilterYears(this.batches);
    // Deep-link: /#/lmg/dms?batch=<batch_number> opens that batch's detail
    // (used by the DMS-number link on the transaction list).
    const q = this.$route && this.$route.query ? this.$route.query.batch : "";
    if (q) {
      const row = (this.batches || []).find((b) => String(b.batch_number) === String(q));
      if (row) this.openDetail(row);
    }
    // Insurance catalog for the layered product-picker filter (same sources as
    // the sales form). listInsure → sellable leaves; loadCatalog → full tree.
    this.listInsure();
    this.loadCatalog();
  },
  methods: {
    // Detail-view item with no billing behind it (a manual line). Reads both the
    // snake_case API key and the legacy PascalCase spelling, like the other
    // detail columns. Display only.
    isManualDmsItem(row) {
      const id = row.billing_id != null ? row.billing_id : row.BillingID;
      return !(Number(id) > 0);
    },
    ...mapActions({
      loadAvailable: "Dms/loadAvailable",
      createBatch: "Dms/createBatch",
      updateBatch: "Dms/updateBatch",
      loadBatchesAction: "Dms/loadBatches",
      loadDetail: "Dms/loadDetail",
      deleteBatch: "Dms/deleteBatch",
      submitBatch: "Dms/submitBatch",
      exportPdf: "Dms/exportPdf",
      exportXlsx: "Dms/exportXlsx",
      listInsure: "Insure/listData",
      loadCatalog: "Insure/loadCatalog",
    }),
    // Reload the batch list with the toolbar filters applied. All of them are
    // optional and AND together at the BE:
    //   batchNumber : partial LIKE on the batch number
    //   ปี          : the whole calendar year of batch_date
    //   ปี + เดือน  : that one month of batch_date
    // Empty everywhere sends no params = full list, exactly the old unfiltered
    // refresh.
    //
    // Nothing else on the page needs resetting here. This list has no pagination
    // and no row selection (the checkbox table lives in the create modal, on a
    // different data source), so unlike the picker there is no offset or ticked
    // row that a narrower result could strand — which is why the existing
    // batchNumber search resets nothing either, and why this stays consistent
    // with it.
    loadBatches() {
      const q = (this.searchBatchNumber || "").trim();
      const params = {};
      if (q) params.batchNumber = q;
      // A เดือน is never sent without a ปี: the month select is disabled until a
      // year is picked and onFilterYearChange clears it when the year goes away.
      // This is the last line of that defence, so a stale month can't ride along.
      if (this.filterYear) {
        params.year = this.filterYear;
        if (this.filterMonth) params.month = this.filterMonth;
      } else if (this.filterMonth) {
        this.filterMonth = "";
      }
      this.appliedUnfiltered = Object.keys(params).length === 0;
      this.loadBatchesAction(params);
    },
    // Clearing the ปี drops the เดือน with it — a month with no year has no
    // meaning, and leaving one parked in a now-disabled select would make the
    // next search look filtered when it is not.
    onFilterYearChange() {
      if (!this.filterYear) this.filterMonth = "";
    },
    // Reset every toolbar filter and reload the full list. Same affordance as
    // clearing the search box by hand and pressing ค้นหา, in one click.
    clearListFilters() {
      this.searchBatchNumber = "";
      this.filterYear = "";
      this.filterMonth = "";
      this.loadBatches();
    },
    // The ปี of one row, read from the same field the วันที่นำส่ง column shows.
    // 0 when the batch has no batch_date (nullable column) or an unparseable one.
    batchYearOf(row) {
      const y = Number(String(this.formatDateShow(row && row.batch_date)).slice(0, 4));
      return y >= 1900 && y <= 2999 ? y : 0;
    },
    // The years the ปี dropdown offers unconditionally: this year and the
    // FILTER_YEARS_BACK before it. See the constant for why a data-only list is
    // not enough.
    baselineFilterYears() {
      const now = new Date().getFullYear();
      const out = [];
      for (let back = 0; back <= FILTER_YEARS_BACK; back += 1) out.push(now - back);
      return out;
    },
    // The ปี dropdown is the recent-years baseline UNION every year the loaded
    // batches actually carry, newest first — so a batch older than the baseline
    // still becomes selectable on its own, without hardcoding a year that rots.
    //
    // Only a completely UNFILTERED response is a census of which years exist, and
    // only it may replace the harvested part. Every filtered response is a subset
    // by construction — a year search returns one year, and even a batch-number
    // search can happen to hit a single year — so those may only ADD years, never
    // remove them. Replacing from a filtered response would collapse the dropdown
    // to whatever the current result contains and strand the operator with no way
    // back to the other years. The baseline is re-seeded either way, so the
    // control can never render empty (no branch data, or every batch missing a
    // วันที่นำส่ง) — an empty dropdown reads as broken.
    harvestFilterYears(rows) {
      const years = this.appliedUnfiltered ? [] : this.filterYearOptions.slice();
      this.baselineFilterYears().forEach((y) => {
        if (years.indexOf(y) === -1) years.push(y);
      });
      (rows || []).forEach((row) => {
        const y = this.batchYearOf(row);
        if (y && years.indexOf(y) === -1) years.push(y);
      });
      years.sort((a, b) => b - a);
      this.filterYearOptions = years;
    },
    formatBaht(v) {
      return Number(v || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    // thousands-separated integer (row counts) — no decimals, unlike formatBaht
    formatCount(v) {
      return Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 0 });
    },
    formatDateShow(d) {
      if (!d) return "";
      // accept "YYYY-MM-DD..." or ISO; show date part only
      return String(d).slice(0, 10);
    },
    roundLabel(r) {
      const o = this.roundOptions.find((x) => x.ID == r);
      return o ? o.Name : String(r);
    },
    statusLabel(s) {
      if (s == 1) return "แบบร่าง";
      if (s == 9) return this.$t("lmg.dms_status_cancelled");
      return this.$t("lmg.dms_status_submitted");
    },
    statusPillClass(s) {
      if (s == 1) return "calm__pill--danger";
      if (s == 9) return "calm__pill--neutral";
      return "calm__pill--success";
    },
    detailStatusClass(s) {
      if (s == 9) return "status-pill status-pill--danger";
      return "status-pill status-pill--success";
    },
    isSelected(row) {
      return this.selectedPolicies.some((p) => p.billing_id === row.billing_id);
    },
    onSelectionChange(rows) {
      // Ignore the events our own syncTableSelection() generates: the el-table
      // store emits selection-change synchronously per toggle, so reading a
      // half-applied sync back as user intent would drop every row the loop has
      // not reached yet (they are on-screen, so `offScreen` excludes them, and
      // they are not in `rows` yet either).
      if (this.syncingSelection) return;
      // The el-table only knows about rows in the current `available` set (plus
      // reserve-selection state). Policies selected in a prior search or restored
      // in edit-mode that are NOT in the current result set must be preserved.
      // Merge: keep off-screen selected policies, then apply the table's rows.
      const availableIds = new Set((this.available || []).map((r) => r.billing_id));
      const offScreen = this.selectedPolicies.filter(
        (p) => !availableIds.has(p.billing_id)
      );
      // With reserve-selection the table's `rows` can already include off-screen
      // previously-selected rows, so offScreen.concat(rows) may repeat a
      // billing_id. Dedupe first-wins (off-screen entries lead, preserving their
      // premiumAdjusted/adjustNote) so selectedPolicies is never double-counted.
      const merged = offScreen.concat(rows);
      const seen = new Set();
      this.selectedPolicies = merged.filter((p) => {
        if (seen.has(p.billing_id)) return false;
        seen.add(p.billing_id);
        return true;
      });
      // ensure each selected policy has a default premium/note entry
      this.selectedPolicies.forEach((p) => {
        if (this.premiumAdjusted[p.billing_id] === undefined) {
          this.$set(this.premiumAdjusted, p.billing_id, Number(p.net_amount) || 0);
        }
        if (this.adjustNote[p.billing_id] === undefined) {
          this.$set(this.adjustNote, p.billing_id, "");
        }
      });
      // ...and drop the entries of policies that are no longer selected, so
      // unticking a row in the table leaves EXACTLY the state the ✕ leaves
      // (removeSelected $deletes them). Without this the two paths diverge and a
      // stale premium/note would be re-applied if the row were ticked again.
      this.pruneRowMaps();
      // Re-ticking a policy that had been taken undoes the removal the notice
      // describes, so the entry has to go with it.
      this.pruneStolenRemoved();
    },
    // Keep premiumAdjusted / adjustNote keyed to exactly the current selection.
    // Object keys are strings, billing_id is a number — compare as strings.
    pruneRowMaps() {
      const keep = new Set(this.selectedPolicies.map((p) => String(p.billing_id)));
      Object.keys(this.premiumAdjusted).forEach((k) => {
        if (!keep.has(k)) this.$delete(this.premiumAdjusted, k);
      });
      Object.keys(this.adjustNote).forEach((k) => {
        if (!keep.has(k)) this.$delete(this.adjustNote, k);
      });
    },
    // billing_ids the CURRENT search reports as blocked by the batch being
    // edited — i.e. ours, not taken by anyone.
    ownBlockedIds() {
      return new Set(
        this.blockedShown
          .filter((b) => this.isOwnBatch(b))
          .map((b) => Number(b.billing_id))
      );
    },
    // stolenRemoved is a claim about the CURRENT selection: "these are missing
    // from it, and here is who took them". It must not outlive that claim, so
    // every entry is retired the moment it stops being true:
    //   * the billing is back in selectedPolicies — the removal was undone (the
    //     holding batch was cancelled, the policy came back as a tickable row
    //     and the operator re-ticked it). Saying it is missing would be a lie.
    //   * this search now reports the billing on OUR OWN batch — it is not
    //     foreign-held at all, so "taken by another batch" was never true of it.
    // Everything else (a plain search, scrolling, editing a premium) leaves the
    // claim intact, which is the whole point: the notice has to survive long
    // enough for the operator to act on it.
    pruneStolenRemoved(ownIds) {
      if (!this.stolenRemoved.length) return;
      const held = new Set(
        this.selectedPolicies.map((p) => Number(p.billing_id))
      );
      const own = ownIds || this.ownBlockedIds();
      this.stolenRemoved = this.stolenRemoved.filter(
        (s) =>
          !held.has(Number(s.billing_id)) && !own.has(Number(s.billing_id))
      );
    },
    // Operator acknowledgement — the one way to dismiss a warning that is still
    // true. Deliberately explicit: nothing else may retire it on their behalf.
    acknowledgeStolen() {
      this.stolenRemoved = [];
    },
    // Push selectedPolicies (the single source of truth) INTO the el-table for
    // every row currently on screen — ticking the ones we hold and UNTICKING the
    // ones we don't. Two Element-UI 2.4.11 behaviours make this mandatory:
    //   * store.toggleRowSelection() is identity-based (states.selection.indexOf)
    //     and silently does nothing — no selection-change event at all — when the
    //     object handed to it is not the exact one the store holds. Each search
    //     replaces `available` with freshly parsed row objects, so identity drifts.
    //   * reserve-selection keeps a row-key ticked inside states.selection even
    //     after we dropped it from selectedPolicies, so a removed policy would
    //     come back ticked (with no chip) on the next search unless it is
    //     explicitly unticked here.
    // Reconciling the whole visible page makes the table match the card exactly,
    // in one direction only, so neither list can drift away from the other.
    syncTableSelection() {
      const table = this.$refs.pickerTable;
      if (!table) return;
      // A policy ticked earlier in this session can be delivered by someone else
      // in the meantime; the next search then reports it in Datum.blocked instead
      // of Data, and it would sit here as an off-screen chip that makes the whole
      // submit fail. Drop those. Entries pointing at the batch being EDITED are
      // this batch's own items — they must stay (the BE has no edit carve-out).
      //
      // ONE BILLING CAN PRODUCE SEVERAL blocked ENTRIES. The BE's lookup joins
      // dms_batch_item with no per-billing dedupe, so a billing sitting on two
      // live batches yields one entry per batch — and while editing batch 290 a
      // billing that is also on batch 305 arrives as BOTH an own-batch entry and
      // a foreign one. So build the own-batch set FIRST and subtract it: the
      // per-entry test must never outvote the per-billing verdict. Getting this
      // backwards silently drops an item this batch legitimately owns, and
      // DmsBatchUpdate is wipe-and-reinsert (it hard-DELETEs the batch's items
      // AND their branch-money ledger rows, then re-inserts only what the payload
      // carries) — so the dropped item's ledger OUT row would vanish with no
      // compensating entry and the branch pool would move unnoticed.
      const ownIds = new Set(
        this.blockedShown
          .filter((b) => this.isOwnBatch(b))
          .map((b) => Number(b.billing_id))
      );
      const stolenBy = new Map();
      this.blockedShown
        .filter((b) => !this.isOwnBatch(b))
        .forEach((b) => {
          const id = Number(b.billing_id);
          if (ownIds.has(id)) return; // also on THIS batch → ours, keep it
          if (!stolenBy.has(id)) stolenBy.set(id, b);
        });
      if (
        stolenBy.size &&
        this.selectedPolicies.some((p) => stolenBy.has(Number(p.billing_id)))
      ) {
        // Never mutate the selection silently — the operator is about to submit a
        // settlement document that no longer contains what they picked. Record
        // what was taken and by which batch, and say so above the results.
        const takenNow = this.selectedPolicies
          .filter((p) => stolenBy.has(Number(p.billing_id)))
          .map((p) => {
            const b = stolenBy.get(Number(p.billing_id));
            return {
              billing_id: p.billing_id,
              policy_number: p.policy_number || b.policy_number || "",
              transaction_id: p.transaction_id,
              batch_id: b.batch_id,
              batch_number: b.batch_number,
              batch_status: b.batch_status,
            };
          });
        // ACCUMULATE, don't overwrite. Each search only sees the policies that
        // match ITS filter, so overwriting would silently retire a still-true
        // warning from an earlier search — which is exactly the silence this
        // notice exists to prevent. Dedupe by billing_id, newest observation
        // wins (the batch that holds it may have changed since).
        const takenIds = new Set(takenNow.map((t) => Number(t.billing_id)));
        this.stolenRemoved = this.stolenRemoved
          .filter((s) => !takenIds.has(Number(s.billing_id)))
          .concat(takenNow);
        this.selectedPolicies = this.selectedPolicies.filter(
          (p) => !stolenBy.has(Number(p.billing_id))
        );
        this.pruneRowMaps();
      }
      // Retire entries this search has just disproved (see pruneStolenRemoved).
      // Runs on EVERY sync, not only when something was taken: a search that
      // finds nothing blocked is exactly what un-blocks a stale entry.
      this.pruneStolenRemoved(ownIds);
      const ids = new Set(this.selectedPolicies.map((p) => p.billing_id));
      this.syncingSelection = true;
      try {
        // Rebuild rather than toggle. reserve-selection makes states.selection
        // retain row objects that are no longer on screen, and those stale
        // entries are unreachable by identity — a policy dropped while off
        // screen would be merged straight back in by the next selection-change.
        // Clearing first makes states.selection a pure projection of
        // selectedPolicies onto the visible page, so it can never hold anything
        // this component didn't just put there.
        table.clearSelection();
        (this.available || []).forEach((r) => {
          if (ids.has(r.billing_id)) table.toggleRowSelection(r, true);
        });
      } finally {
        this.syncingSelection = false;
      }
    },
    // Remove one policy from the selected card. selectedPolicies is authoritative:
    // splice it here and then reconcile the table, instead of asking the el-table
    // to untick and waiting for selection-change to come back (that round trip is
    // what made the ✕ appear dead — see syncTableSelection for why the event can
    // fail to fire). Works identically whether or not the row is in the current
    // result set, and ends in exactly the state an untick-in-the-table produces.
    removeSelected(policy) {
      const idx = this.selectedPolicies.findIndex(
        (p) => p.billing_id === policy.billing_id
      );
      if (idx !== -1) this.selectedPolicies.splice(idx, 1);
      // clean up per-row editable maps
      this.$delete(this.premiumAdjusted, policy.billing_id);
      this.$delete(this.adjustNote, policy.billing_id);
      // untick it in the table (and clear the reserve-selection key) so it can't
      // resurrect on the next search
      this.syncTableSelection();
    },
    // True when a blocked entry points at the batch the operator is editing right
    // now. The BE's blocked lookup has no edit-mode carve-out, so while editing
    // batch 290 a search for one of ITS OWN policies comes back "already in batch
    // 290" — accurate but baffling. Word it as "already on this delivery note".
    isOwnBatch(b) {
      return !!(
        this.editingBatchId && Number(b.batch_id) === Number(this.editingBatchId)
      );
    },
    // DMS_BATCH_STATUS_* (WsDmsBatchORM.go). 9/cancelled never reaches `blocked`.
    batchStatusLabel(s) {
      const n = Number(s);
      if (n === 1) return "แบบร่าง";
      if (n === 2) return this.$t("lmg.dms_status_submitted");
      if (n === 3) return "ตอบกลับแล้ว";
      if (n === 4) return "มีปัญหา";
      if (n === 5) return "กระทบยอดแล้ว";
      if (n === 9) return this.$t("lmg.dms_status_cancelled");
      return String(s == null ? "" : s);
    },
    // Pill tone for a blocked entry's batch: draft = "ยังไม่นำส่ง" (red, the same
    // signal the list uses), problem = warning, everything else = delivered.
    batchStatusPillClass(s) {
      const n = Number(s);
      if (n === 1) return "calm__pill--danger";
      if (n === 4) return "calm__pill--warning";
      if (n === 9) return "calm__pill--neutral";
      return "calm__pill--success";
    },
    filesChange(files) {
      this.pdfFile = files && files.length ? files[0] : null;
    },
    openCreate() {
      this.showCreateModal = true;
      // create flow — never leak edit state into a fresh create
      this.editingBatchId = null;
      // reset state to avoid stale carryover
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      this.form.batchNumber = "";
      this.form.batchDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      this.form.round = "";
      this.form.month = now.getMonth() + 1;
      this.form.year = now.getFullYear();
      this.form.note = "";
      this.form.policySearch = "";
      this.form.insuranceNodeID = 0;
      this.form.insuranceIDs = "";
      this.form.saleStart = "";
      this.form.saleEnd = "";
      this.pdfFile = null;
      this.selectedPolicies = [];
      this.premiumAdjusted = {};
      this.adjustNote = {};
      // Search-on-demand: start the picker empty — staff types a policy filter
      // and clicks search themselves (no auto-load of all available policies).
      this.isSearchingPolicies = false;
      this.hasSearchedPolicies = false;
      this.$store.commit("Dms/SET_AVAILABLE", []);
      this.$store.commit("Dms/SET_AVAILABLE_TOTAL", 0);
      this.$store.commit("Dms/SET_BLOCKED", []);
      this.$store.commit("Dms/SET_BLOCKED_TOTAL", 0);
      this.stolenRemoved = [];
      this.syncingSelection = false;
      this.$nextTick(() => {
        if (!this.$refs.pickerTable) return;
        // clearSelection() emits selection-change([]); guard it so it can never
        // clobber an edit-mode restore that landed first.
        this.syncingSelection = true;
        try {
          this.$refs.pickerTable.clearSelection();
        } finally {
          this.syncingSelection = false;
        }
      });
    },
    // Edit flow: open the SAME create modal pre-filled from GET /dms/batch/:id.
    // Header fields + selected items (mapped to the picker row shape) are
    // restored; premiumAdjusted/adjustNote are keyed by billing_id. Ticks are
    // re-applied via reserve-selection when a matching row appears in a search.
    async openEdit(row) {
      this.openCreate(); // reset to a clean slate first
      this.editingBatchId = row.id;
      let datum = row;
      try {
        const res = await this.loadDetail(row.id);
        const d = (res.data && (res.data.Datum || res.data.Data)) || null;
        if (d && (d.id || d.batch_number)) datum = d;
      } catch (err) {
        // fall back to the list-row data
      }
      // header fields
      this.form.batchNumber = datum.batch_number || "";
      this.form.batchDate = this.formatDateShow(datum.batch_date) || this.form.batchDate;
      this.form.round = datum.round || "";
      if (datum.month) this.form.month = Number(datum.month);
      if (datum.year) this.form.year = Number(datum.year);
      this.form.note = datum.note || "";
      // selected items → picker row shape + per-row editable maps
      const items = datum.Items || datum.items || [];
      const rows = items.map((it) => {
        const bid = it.billing_id != null ? it.billing_id : it.BillingID;
        const tid = it.transaction_id != null ? it.transaction_id : it.TransactionID;
        const pn = it.policy_number || it.PolicyNumber || "";
        const orig = it.premium_original != null ? it.premium_original : it.PremiumOriginal;
        const adj = it.premium_adjusted != null ? it.premium_adjusted : it.PremiumAdjusted;
        const note = it.adjust_note || it.AdjustNote || "";
        this.$set(this.premiumAdjusted, bid, Number(adj != null ? adj : orig) || 0);
        this.$set(this.adjustNote, bid, note);
        return {
          billing_id: bid,
          transaction_id: tid,
          policy_number: pn,
          net_amount: orig != null ? orig : adj,
          customer_fname: it.customer_fname || it.CustomerFname || "",
          customer_lname: it.customer_lname || it.CustomerLname || "",
          agent_fname: it.agent_fname || it.AgentFname || "",
          agent_lname: it.agent_lname || it.AgentLname || "",
          insurance_name: it.insurance_name || it.InsuranceName || "",
        };
      });
      // Defensively dedupe by billing_id (first-wins) so a legacy already-
      // duplicated batch can't reload duplicate items into the editor.
      const seenBids = new Set();
      this.selectedPolicies = rows.filter((r) => {
        if (seenBids.has(r.billing_id)) return false;
        seenBids.add(r.billing_id);
        return true;
      });
    },
    // Search-on-demand: fetch available policies matching the typed policy-number
    // filter (BE param `policy`) + optional round. Ticks from prior searches are
    // preserved by row-key + reserve-selection on the el-table.
    searchPolicies() {
      this.isSearchingPolicies = true;
      // NOTE: stolenRemoved is deliberately NOT cleared here. The removal it
      // reports outlives the search that discovered it, so clearing on every
      // search let the operator submit a batch quietly missing lines with
      // nothing left on screen to say why — the precise failure this notice was
      // added to prevent. It is retired by acknowledgement (acknowledgeStolen),
      // by becoming untrue (pruneStolenRemoved), or by the selection itself
      // going away (openCreate / a saved batch).
      this.loadAvailable({
        policy: this.form.policySearch || "",
        round: this.form.round || "",
        insuranceIDs: this.form.insuranceIDs || "",
        startDate: this.form.saleStart || "",
        endDate: this.form.saleEnd || "",
      })
        .then(() => {
          this.hasSearchedPolicies = true;
          // el-table recomputes column widths from the DOM; re-run after data swap.
          this.$nextTick(() => {
            if (!this.$refs.pickerTable) return;
            this.$refs.pickerTable.doLayout();
            // Reconcile the fresh result set against selectedPolicies: tick the
            // rows we hold (edit-mode pre-selection + prior searches, which
            // reserve-selection can't preserve for rows it never saw ticked) and
            // untick the rest (reserve-selection would otherwise resurrect a
            // policy the operator already removed).
            this.syncTableSelection();
          });
        })
        .finally(() => {
          this.isSearchingPolicies = false;
        });
    },
    // Insurance-type filter pick. The picker emits the chosen catalog NODE (any
    // layer). We store its ID for the trigger, and expand it into every leaf
    // product ID beneath it (incl. itself) → CSV sent to BE as `insuranceIDs`,
    // so "เลือกหมวด" filters all products under that layer.
    onPickInsuranceFilter(node) {
      if (node) {
        this.form.insuranceNodeID = node.ID;
        this.form.insuranceIDs = this.collectSubtreeIds(node.ID).join(",");
      } else {
        this.form.insuranceNodeID = 0;
        this.form.insuranceIDs = "";
      }
    },
    // BFS over catalogChildrenOf (Map UpperID→node[]) collecting the node and
    // all descendant IDs. Cycle-guarded. Returns [] if the tree isn't loaded.
    collectSubtreeIds(rootID) {
      const out = [];
      if (rootID == null) return out;
      const seen = new Set();
      const stack = [Number(rootID)];
      while (stack.length) {
        const id = stack.pop();
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(id);
        const kids =
          (this.catalogChildrenOf && this.catalogChildrenOf.get(id)) || [];
        for (const k of kids) stack.push(Number(k.ID));
      }
      return out;
    },
    // Reset all picker filters without closing the modal (the "ล้างตัวกรอง" btn).
    clearPickerFilters() {
      this.form.insuranceNodeID = 0;
      this.form.insuranceIDs = "";
      this.form.policySearch = "";
      this.form.saleStart = "";
      this.form.saleEnd = "";
      this.form.round = "";
    },
    async openDetail(row) {
      this.detail = row;
      this.detailItems = row.Items || row.items || [];
      this.showDetailModal = true;
      if (row && row.id) {
        try {
          const res = await this.loadDetail(row.id);
          const datum = (res.data && (res.data.Datum || res.data.Data)) || {};
          if (datum && (datum.id || datum.batch_number)) this.detail = datum;
          this.detailItems = (datum && (datum.Items || datum.items)) || this.detailItems;
        } catch (err) {
          // keep list-row data as fallback
        }
      }
    },
    viewFile(row) {
      LmgService.DmsBatchFile(row.id)
        .then((res) => {
          const type = res.headers && res.headers["content-type"] ? res.headers["content-type"] : "application/pdf";
          const blob = new Blob([res.data], { type });
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank");
        })
        .catch(() => {
          swal.fire({ title: "Error", text: "เปิดไฟล์ไม่สำเร็จ", icon: "error" });
        });
    },
    // per-batch export button loading state — keyed by `${id}:${kind}`
    isExporting(id, kind) {
      return !!this.exportingKeys[id + ":" + kind];
    },
    // Export a DMS batch as PDF (formatted document) or XLSX (raw record) and
    // trigger a browser download. Mirrors the WhtTables export idiom:
    // createObjectURL(blob) → temp <a download> → click → revoke.
    exportBatch(row, kind) {
      const key = row.id + ":" + kind;
      if (this.exportingKeys[key]) return;
      this.$set(this.exportingKeys, key, true);

      const action = kind === "xlsx" ? this.exportXlsx : this.exportPdf;
      const ext = kind === "xlsx" ? "xlsx" : "pdf";
      const mime =
        kind === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";
      const filename = "DMS-" + (row.batch_number || row.id) + "." + ext;

      action(row.id)
        .then((res) => {
          const type =
            res.headers && res.headers["content-type"]
              ? res.headers["content-type"]
              : mime;
          const blob = new Blob([res.data], { type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          swal.fire({ title: "Error", text: "ดาวน์โหลดไม่สำเร็จ", icon: "error" });
        })
        .finally(() => {
          this.$delete(this.exportingKeys, key);
        });
    },
    // True while ANY mutation (นำส่ง/ลบ) is outstanding for this batch.
    isRowBusy(id) {
      return !!this.rowBusy[id];
    },
    // True while THIS specific mutation is outstanding — drives the spinner so
    // the operator can see which of the two disabled buttons is the live one.
    isRowBusyWith(id, action) {
      return this.rowBusy[id] === action;
    },
    confirmDelete(row) {
      // Same exposure as confirmSubmit: swal resolves as soon as the operator
      // confirms, long before the request does, so the row stays re-clickable.
      if (this.rowBusy[row.id]) return;
      swal
        .fire({
          title: this.$t("lmg.dms_delete_confirm"),
          text: row.batch_number || ("#" + row.id),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("lmg.delete"),
          cancelButtonText: this.$t("close"),
          confirmButtonClass: "btn btn-danger btn-fill",
          cancelButtonClass: "btn btn-secondary btn-fill",
          buttonsStyling: false,
        })
        .then((r) => {
          if (!r.value) return;
          // Re-check: the dialog is async, so a second one may have been opened
          // and confirmed while this one was still on screen.
          if (this.rowBusy[row.id]) return;
          this.$set(this.rowBusy, row.id, "delete");
          this.deleteBatch(row.id)
            .then(() => {
              this.loadBatches();
              swal.fire({ title: this.$t("create_success"), icon: "success", buttonsStyling: false, confirmButtonClass: "btn btn-success btn-fill" });
            })
            .catch((err) => {
              const msg = err && err.response && err.response.data ? err.response.data.Msg : "ลบไม่สำเร็จ";
              swal.fire({ title: this.$t(msg), icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
            })
            // Success, HTTP error AND network error alike — a button that never
            // comes back is its own outage for the operator.
            .finally(() => {
              this.$delete(this.rowBusy, row.id);
            });
        });
    },
    // นำส่ง draft → submitted: BE applies the ledger (ตัดยอดเงินกองกลางสาขา).
    // Single-fire per batch — see `rowBusy`. Guarded in three places because the
    // window is genuinely open at each: here (the row button is live again the
    // moment the dialog closes), after the confirm resolves (a second dialog may
    // have been opened behind this one), and via :disabled on the button itself.
    confirmSubmit(row) {
      if (this.rowBusy[row.id]) return;
      swal
        .fire({
          title: "ยืนยันนำส่ง",
          text:
            (row.batch_number || "#" + row.id) +
            " · จะตัดยอดเงินกองกลางสาขา",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "นำส่ง",
          cancelButtonText: this.$t("close"),
          confirmButtonClass: "btn btn-primary btn-fill",
          cancelButtonClass: "btn btn-secondary btn-fill",
          buttonsStyling: false,
        })
        .then((r) => {
          if (!r.value) return;
          if (this.rowBusy[row.id]) return;
          this.$set(this.rowBusy, row.id, "submit");
          this.submitBatch(row.id)
            .then(() => {
              this.loadBatches();
              swal.fire({
                title: "นำส่งสำเร็จ",
                icon: "success",
                buttonsStyling: false,
                confirmButtonClass: "btn btn-success btn-fill",
              });
            })
            .catch((err) => {
              const msg =
                err && err.response && err.response.data
                  ? err.response.data.Cause || err.response.data.Msg
                  : "นำส่งไม่สำเร็จ";
              swal.fire({
                title: "Error",
                text: msg,
                icon: "error",
                buttonsStyling: false,
                confirmButtonClass: "btn btn-danger btn-fill",
              });
            })
            // Clear on EVERY outcome, network failure included. A batch stuck
            // behind a permanently-disabled นำส่ง button cannot be delivered at
            // all, which is the worse of the two failures.
            .finally(() => {
              this.$delete(this.rowBusy, row.id);
            });
        });
    },
    validatePdf() {
      if (!this.pdfFile) return true; // optional
      if (this.pdfFile.size > MAX_PDF_BYTES) {
        swal.fire({ title: "ไฟล์ใหญ่เกินไป", text: "ต้องไม่เกิน 5MB", icon: "error" });
        return false;
      }
      const okType = this.pdfFile.type === "application/pdf" || /^image\//.test(this.pdfFile.type || "");
      if (!okType) {
        swal.fire({ title: "ชนิดไฟล์ไม่รองรับ", text: "รับเฉพาะ PDF หรือรูปภาพ", icon: "error" });
        return false;
      }
      return true;
    },
    // status: 1 = draft (บันทึกร่าง), 2 = submitted (นำส่ง). Only sent on the
    // create path — the edit path is status-preserving (BE keeps existing status;
    // the dedicated นำส่ง row action handles draft→submitted).
    submit(status) {
      if (!this.selectedPolicies.length) return;
      if (!this.form.batchNumber) {
        swal.fire({ title: this.$t("lmg.batch_number"), text: "กรุณากรอกเลขที่ใบนำส่ง", icon: "error" });
        return;
      }
      if (!this.form.batchDate) {
        swal.fire({ title: this.$t("lmg.batch_date"), text: "กรุณาเลือกวันที่นำส่ง", icon: "error" });
        return;
      }
      if (!this.validatePdf()) return;

      const items = this.selectedPolicies.map((p) => ({
        billing_id: p.billing_id,
        transaction_id: p.transaction_id,
        policy_number: p.policy_number,
        premium_original: Number(p.net_amount) || 0,
        premium_adjusted: Number(
          this.premiumAdjusted[p.billing_id] != null ? this.premiumAdjusted[p.billing_id] : p.net_amount
        ),
        adjust_note: this.adjustNote[p.billing_id] || "",
      }));

      const fd = new FormData();
      fd.append("batch_number", this.form.batchNumber);
      fd.append("batch_date", this.form.batchDate);
      fd.append("round", this.form.round || "");
      fd.append("month", this.form.month);
      fd.append("year", this.form.year);
      fd.append("note", this.form.note || "");
      // Omit Pdf on edit when no new file chosen → backend keeps the existing attachment.
      if (this.pdfFile) fd.append("Pdf", this.pdfFile);
      fd.append("Items", JSON.stringify(items));

      this.submitting = true;
      const wasEdit = !!this.editingBatchId;
      // status only applies to the create path; edit keeps the batch's existing status.
      if (!wasEdit) fd.set("status", status == 1 ? 1 : 2);
      const req = wasEdit
        ? this.updateBatch({ id: this.editingBatchId, formData: fd })
        : this.createBatch(fd);
      req
        .then(() => {
          this.showCreateModal = false;
          this.editingBatchId = null;
          // The selection this warning described has been committed — it now
          // describes nothing. (openCreate/openEdit reset it too; this keeps the
          // saved batch from being the one path that leaves it hanging.)
          this.stolenRemoved = [];
          // A brand-new batch number almost never matches the filter that is
          // still in the box, so a filtered reload would hide the batch that was
          // just created. Clear the filter on create. Edit keeps it: the batch
          // number IS editable (DmsBatchUpdate persists it), so renaming a batch
          // out of the active filter does drop it from the filtered view — that
          // reads as correct (it no longer matches what was searched for), and
          // clearing the box there would be surprising in the other direction.
          if (!wasEdit) this.searchBatchNumber = "";
          this.loadBatches();
          swal.fire({
            title: wasEdit
              ? "แก้ไขใบนำส่ง DMS สำเร็จ"
              : status == 1
              ? "บันทึกร่างสำเร็จ"
              : this.$t("create_success"),
            icon: "success",
            buttonsStyling: false,
            confirmButtonClass: "btn btn-success btn-fill",
          });
        })
        .catch((err) => {
          const msg = err && err.response && err.response.data ? err.response.data.Cause || err.response.data.Msg : "บันทึกไม่สำเร็จ";
          swal.fire({ title: "Error", text: msg, icon: "error" });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.dms-num >>> input { text-align: right; }
.dms-deleted { font-size: 12px; }
/* draft batches: bold + Calm-red batch number to flag "ยังไม่นำส่ง" */
.dms-draft-number { font-weight: 700; color: var(--c-danger, #c0573e); }
/* keep the policy picker table at its natural column widths and scroll
   horizontally instead of squishing columns (checkbox was overlapping the
   policy-number text inside the narrow modal).
   Operator item "ออกมาด้านนอก": the column min-widths now sum to 1028px
   (was 1105px — just over modal-xl's ~1088px content box, which is what put a
   horizontal scrollbar under every search result), and the picker dialog is
   widened below, so at ≥1200px viewports the whole row fits with no sideways
   scroll. overflow-x stays as the fallback for narrower screens only. */
.bm-table-wrap { overflow-x: auto; }
/* policy numbers (24-AV1-1240-2026-06) must stay on one line — they wrapped
   mid-number in the operator's screenshot. */
.dms-nowrap { white-space: nowrap; }

/* ── Problem A: keep the search row reachable with 70+ policies ticked ──
 * Two independent measures, because either alone is not enough:
 *   1) the search row is sticky at the top of the picker section, so scrolling
 *      down through the results never takes it off screen;
 *   2) the selected-policies card is height-capped with its own scrollbar, so it
 *      can't grow to hundreds of chips and shove the results table out of view.
 * The shared `.bm-section--table { overflow: hidden }` rule (declared unscoped in
 * v2/InvoiceTables.vue) would make position:sticky inert, so the picker section
 * opts out of it and the table wrapper re-clips the card's rounded corners. */
.bm-section--picker { overflow: visible; }
.bm-section--picker .bm-table-wrap {
  overflow: auto;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}
/* policy-picker search-on-demand bar */
.bm-picker-search {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  position: sticky;
  top: 0;
  z-index: 5;
  margin: 0;
  padding: 12px 20px;
  background: var(--c-surface, #fff);
  border-bottom: 1px solid var(--c-border, #e6e8ef);
}
.bm-picker-search__input { flex: 1 1 240px; min-width: 200px; }
.bm-picker-search__round { width: 200px; }
.bm-picker-search__insure { flex: 1 1 260px; min-width: 220px; }
.bm-picker-search__date { flex: 0 0 150px; width: 150px; }
.bm-picker-search__count { font-size: 13px; margin-left: auto; }
/* truncation warning pill — .calm__pill is nowrap by default, but this line is
   long enough to overflow the narrow modal header, so let it wrap. */
.dms-trunc { white-space: normal; }

/* selected-items card — chips with a per-item × remove (create/edit picker) */
.bm-selected {
  border: 1px solid var(--c-border, #e6e8ef);
  border-radius: 8px;
  background: var(--c-blue-soft, #f4f7ff);
  padding: 10px 12px;
  margin: 14px 20px;
}
.bm-selected__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  margin-bottom: 8px;
}
/* per-type chips inside the selected card head: no extra vertical margin */
.dms-type-summary--picker { margin: 0; gap: 6px; }
.bm-selected__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  /* ≈4 rows of chips, then scroll inside the card — see the Problem A note above.
     A 71-policy batch would otherwise be ~600px of chips between the search row
     and the results table. */
  max-height: 148px;
  overflow-y: auto;
  padding-right: 4px;
}
.bm-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid var(--c-border, #e6e8ef);
  border-radius: 999px;
  padding: 4px 6px 4px 12px;
  font-size: 12.5px;
  max-width: 100%;
}
.bm-chip__policy { font-weight: 600; }
.bm-chip__name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bm-chip__premium { color: var(--c-text-soft, #6b7280); }
.bm-chip__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--c-text-soft, #6b7280);
  cursor: pointer;
  transition: all var(--c-fast, 0.15s) var(--c-ease, ease);
}
.bm-chip__x:hover { background: #fee2e2; color: #dc2626; }

/* "already in a DMS batch" notice — these are NOT table rows (the BE keeps them
   out of Data on purpose), so they read as an explanation sitting above the
   results, never as something tickable. */
.bm-blocked {
  margin: 0 20px 14px;
  padding: 10px 12px;
  border: 1px solid #f0c9b8;
  border-radius: 8px;
  background: #fdf1ec;
  color: #8f4526;
  font-size: 12.5px;
}
.bm-blocked__head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}
.bm-blocked__head i { color: #b0512f; }
.bm-blocked__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  border-top: 1px solid rgba(176, 81, 47, 0.16);
}
.bm-blocked__policy { font-weight: 600; }
.bm-blocked__where { color: #8f4526; }
.bm-blocked__date { font-size: 12px; }
.bm-blocked__more { margin-top: 8px; font-size: 12px; }
/* stronger tone: this one says something was taken OUT of the operator's batch */
.bm-blocked--warn {
  border-color: #e0b400;
  background: #fff8e1;
  color: #7a5b00;
}
.bm-blocked--warn .bm-blocked__head i { color: #b58900; }
/* acknowledgement: pushed to the far end of the head row so it reads as a
   dismissal of the warning, not as an action on the batch. */
.bm-blocked__ack {
  margin-left: auto;
  flex: none;
  padding: 2px 10px;
  border: 1px solid rgba(181, 137, 0, 0.55);
  border-radius: 6px;
  background: transparent;
  color: #7a5b00;
  font-size: 12px;
  cursor: pointer;
}
.bm-blocked__ack:hover { background: rgba(181, 137, 0, 0.14); }
.bm-blocked--warn .bm-blocked__row { border-top-color: rgba(181, 137, 0, 0.22); }
.bm-blocked--warn .bm-blocked__where { color: #7a5b00; }

/* per-insurance-type count summary (detail modal) */
.dms-type-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 10px;
}
.dms-type-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--c-blue-soft, #f4f7ff);
  border: 1px solid var(--c-border, #e6e8ef);
  font-size: 12.5px;
}
</style>

<style>
/* Row-action icon buttons — match the v2 Invoice page theme
 * (calm ghost icon button: square, transparent, blue tint on hover).
 * Keyed under .calm (the page root) so it can't bleed onto other pages.
 * Unscoped to mirror v2/InvoiceTables.vue exactly. */
.calm .row-actions {
  display: inline-flex;
  gap: 4px;
  justify-content: flex-end;
}
.calm .rowbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  color: var(--c-text-soft);
  border-radius: 6px;
  cursor: pointer;
  transition: all var(--c-fast) var(--c-ease);
  font-size: 12.5px;
}
.calm .rowbtn:hover {
  background: var(--c-blue-soft);
  border-color: var(--c-blue);
  color: var(--c-blue-deep);
}

/* Make the PICKER modal's body the scroll container.
 *
 * Without this the nearest scrolling ancestor is Bootstrap's `.modal`
 * (`.modal-open .modal { overflow-y: auto }`, position:fixed, full viewport), so
 * `.bm-picker-search`'s `top: 0` resolves against the VIEWPORT. That still pins
 * — the sticky bar can never reach the backdrop, because its containing block
 * `.bm-section--picker` is inside `.modal-content`, which therefore always spans
 * y=0 while the bar is stuck — but it pins flush to the screen edge with the
 * card's top chrome scrolled away, and the footer (บันทึกร่าง / นำส่ง) scrolls
 * off with it, which is a long way back up at 71 policies.
 *
 * Bounding the body makes the scrollport the modal card itself: the bar pins
 * just under the card's top edge, and header + footer stay put.
 *
 * Scoped to .dms-picker-modal on purpose — `.calm-modal` is shared with
 * v2/InvoiceTables.vue's create modal, which must not change.
 * 210px ≈ dialog margins (56) + footer (~62) + slack. */
.dms-picker-modal .modal-body {
  overflow-y: auto;
  max-height: calc(100vh - 210px);
}
/* Widen the picker dialog beyond modal-xl's 1140px on wide screens so the
 * results table fits without a horizontal scrollbar (operator item
 * "ออกมาด้านนอก"). Same scoping rule as above: .dms-picker-modal only. */
@media (min-width: 1200px) {
  /* modal-classes lands on .modal-dialog itself (components/Modal.vue) */
  .dms-picker-modal.modal-dialog {
    max-width: min(1320px, 96vw);
  }
}

</style>
